import { useState } from 'react'
import {
   ICadenceStep,
   TCadenceAction,
} from '@forwardflow/features/campaign-v2/interfaces/campaign-v2.types'
import { useCampaignV2Store } from '@forwardflow/features/campaign-v2/store/editor.store'
import {
   addCadenceStep,
   renumber,
} from '@forwardflow/features/campaign-v2/utils/config'
import { FlowIcon, TFlowIcon } from '../ui/FlowIcon'
import {
   buildCadence,
   CadenceRecipe,
   editCadence,
   moveCadence,
   PRESETS,
} from './model'
const label: Record<TCadenceAction, string> = {
   call: 'Call',
   sms: 'SMS',
   email: 'Email',
}
const icon: Record<TCadenceAction, TFlowIcon> = {
   call: 'phone',
   sms: 'chat',
   email: 'send',
}
export const Cadence = ({ onContent }: { onContent: () => void }) => {
   const { config, setConfig } = useCampaignV2Store()
   const steps = renumber(config?.cadence || [])
   const [selected, setSelected] = useState<string | null>(null)
   const [builder, setBuilder] = useState(false)
   const [recipe, setRecipe] = useState<CadenceRecipe>({ ...PRESETS[0].recipe })
   const [preview, setPreview] = useState<ICadenceStep[] | null>(null)
   const [error, setError] = useState('')
   const [dragged, setDragged] = useState<string | null>(null)
   const current = steps.find((s) => s.id === selected)
   const timezone =
      typeof config?.schedulePolicy?.timezone === 'string'
         ? config.schedulePolicy.timezone
         : 'Organization timezone'
   const change = (cadence: ICadenceStep[]) =>
      setConfig((c) => ({ ...c, cadence }))
   const update = (patch: Partial<ICadenceStep>) =>
      current && change(editCadence(steps, current.id, patch))
   const makePreview = (r: CadenceRecipe) => {
      try {
         setPreview(buildCadence(r))
         setError('')
      } catch (e) {
         setError((e as Error).message)
      }
   }
   const add = (action: TCadenceAction) => {
      const next = addCadenceStep(steps, action)
      change(next)
      setSelected(next[next.length - 1].id)
   }
   const cards = (items: ICadenceStep[], editable: boolean) => (
      <div className="cw-timeline">
         <div className="cw-track">
            {items.map((s) => (
               <button
                  type="button"
                  key={s.id}
                  className="cw-touch"
                  draggable={editable}
                  aria-pressed={editable && s.id === selected}
                  onClick={() => editable && setSelected(s.id)}
                  onDragStart={() => setDragged(s.id)}
                  onDragOver={(e) => editable && e.preventDefault()}
                  onDrop={(e) => {
                     e.preventDefault()
                     if (editable && dragged)
                        change(moveCadence(steps, dragged, s.id))
                     setDragged(null)
                  }}
                  data-testid={editable ? `cadence-${s.id}` : undefined}
               >
                  <div className="flex items-center justify-between">
                     <small>Day {s.day}</small>
                     {editable && <FlowIcon name="pen" size={12} />}
                  </div>
                  <strong>
                     <FlowIcon name={icon[s.action]} size={17} />
                     {label[s.action]}
                  </strong>
                  <time>{s.localTime}</time>
                  {s.action === 'call' && s.voicemail === 'if-configured' && (
                     <span className="cw-voicemail">Voicemail on</span>
                  )}
               </button>
            ))}
         </div>
      </div>
   )
   return (
      <div data-testid="workspace-cadence">
         <div className="cw-title">
            <div>
               <h2>Cadence</h2>
               <p>One timeline shared by all lead types.</p>
            </div>
            <div className="cw-actions">
               <select
                  aria-label="Use a cadence template"
                  className="cw-select !w-auto !min-h-9 !py-2 !text-xs"
                  value=""
                  onChange={(e) => {
                     const p = PRESETS[Number(e.target.value)]
                     if (p) {
                        setRecipe(p.recipe)
                        setBuilder(true)
                        makePreview(p.recipe)
                     }
                  }}
               >
                  <option value="">Use a template</option>
                  {PRESETS.map((p, i) => (
                     <option value={i} key={p.name}>
                        {p.name}
                     </option>
                  ))}
               </select>
               <button
                  className="cw-button"
                  type="button"
                  onClick={() => setBuilder(!builder)}
                  data-testid="build-cadence"
               >
                  Build cadence
               </button>
               <select
                  aria-label="Add a cadence step"
                  className="cw-select !w-auto !min-h-9 !py-2 !text-xs"
                  value=""
                  onChange={(e) => add(e.target.value as TCadenceAction)}
               >
                  <option value="">+ Add step</option>
                  {Object.entries(label).map(([v, l]) => (
                     <option key={v} value={v}>
                        {l}
                     </option>
                  ))}
               </select>
            </div>
         </div>
         {builder && (
            <div className="cw-surface" data-testid="cadence-builder">
               <div className="flex items-center justify-between">
                  <h3>Build a cadence</h3>
                  <button
                     type="button"
                     className="cw-link"
                     onClick={() => {
                        setBuilder(false)
                        setPreview(null)
                     }}
                  >
                     Close
                  </button>
               </div>
               <p className="cw-inline-note">
                  Choose the mix, then review the timeline before applying it.
               </p>
               <div className="cw-grid">
                  {(['days', 'call', 'sms', 'email'] as const).map((k) => (
                     <label className="cw-field" key={k}>
                        {
                           {
                              days: 'Days',
                              call: 'Calls',
                              sms: 'Texts',
                              email: 'Emails',
                           }[k]
                        }
                        <input
                           type="number"
                           min={k === 'days' ? 1 : 0}
                           max={k === 'days' ? 365 : 100}
                           value={recipe[k]}
                           onChange={(e) => {
                              setRecipe({
                                 ...recipe,
                                 [k]: Number(e.target.value),
                              })
                              setPreview(null)
                           }}
                        />
                     </label>
                  ))}
                  <label className="cw-field">
                     Start with
                     <select
                        value={recipe.start}
                        onChange={(e) => {
                           setRecipe({
                              ...recipe,
                              start: e.target.value as TCadenceAction,
                           })
                           setPreview(null)
                        }}
                     >
                        {Object.entries(label).map(([v, l]) => (
                           <option key={v} value={v}>
                              {l}
                           </option>
                        ))}
                     </select>
                  </label>
                  <label className="cw-field">
                     First time of day
                     <input
                        type="time"
                        value={recipe.time}
                        onChange={(e) => {
                           setRecipe({ ...recipe, time: e.target.value })
                           setPreview(null)
                        }}
                     />
                  </label>
               </div>
               <label className="cw-checkbox mt-5">
                  <input
                     type="checkbox"
                     checked={recipe.voicemail}
                     onChange={(e) => {
                        setRecipe({ ...recipe, voicemail: e.target.checked })
                        setPreview(null)
                     }}
                  />
                  Allow voicemail on call steps
               </label>
               {error && (
                  <p className="cw-inline-note text-red-300" role="alert">
                     {error}
                  </p>
               )}
               <div className="mt-5">
                  <button
                     type="button"
                     className="cw-button"
                     onClick={() => makePreview(recipe)}
                  >
                     Preview cadence
                  </button>
               </div>
               {preview && (
                  <div className="mt-5">
                     <p className="cw-inline-note">
                        Proposed timeline · Your existing content stays
                        selected.
                     </p>
                     {cards(preview, false)}
                     <p className="cw-inline-note">
                        Applying this replaces the {steps.length} steps in your
                        local draft.
                     </p>
                     <button
                        type="button"
                        className="cw-button primary"
                        onClick={() => {
                           change(preview)
                           setPreview(null)
                           setBuilder(false)
                           setSelected(null)
                        }}
                        data-testid="apply-cadence-preview"
                     >
                        Use this cadence
                     </button>
                  </div>
               )}
            </div>
         )}
         <div className="cw-surface">
            <div className="flex items-center justify-between gap-4">
               <h3>Timeline</h3>
               <span className="cw-muted flex items-center gap-2">
                  <FlowIcon name="clock" size={14} />
                  {timezone}
               </span>
            </div>
            {steps.length ? (
               cards(steps, true)
            ) : (
               <p className="cw-inline-note">
                  Add a step or build a cadence to get started.
               </p>
            )}
            <p className="cw-muted">
               Click a step to edit it. Drag to reorder actions between time
               slots.
            </p>
         </div>
         {current && (
            <div className="cw-surface" data-testid="cadence-step-editor">
               <h3>Edit {label[current.action]} step</h3>
               <div className="cw-grid">
                  <label className="cw-field">
                     Day
                     <input
                        type="number"
                        min={1}
                        max={3650}
                        value={current.day}
                        onChange={(e) => {
                           const v = Number(e.target.value)
                           if (Number.isInteger(v) && v >= 1 && v <= 3650)
                              update({ day: v })
                        }}
                     />
                  </label>
                  <label className="cw-field">
                     Time
                     <input
                        type="time"
                        value={current.localTime}
                        onChange={(e) =>
                           e.target.value &&
                           update({ localTime: e.target.value })
                        }
                     />
                  </label>
                  <label className="cw-field">
                     Action
                     <select
                        value={current.action}
                        onChange={(e) =>
                           update({ action: e.target.value as TCadenceAction })
                        }
                     >
                        {Object.entries(label).map(([v, l]) => (
                           <option key={v} value={v}>
                              {l}
                           </option>
                        ))}
                     </select>
                  </label>
               </div>
               {current.action === 'call' && (
                  <label className="cw-checkbox mt-5">
                     <input
                        type="checkbox"
                        checked={current.voicemail === 'if-configured'}
                        onChange={(e) =>
                           update({
                              voicemail: e.target.checked
                                 ? 'if-configured'
                                 : 'none',
                           })
                        }
                     />
                     Leave voicemail if configured and unanswered
                  </label>
               )}
               <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
                  <button className="cw-link" type="button" onClick={onContent}>
                     View{' '}
                     {current.action === 'call'
                        ? 'outgoing scripts'
                        : 'outbound content groups'}{' '}
                     <FlowIcon name="arrowRight" size={14} />
                  </button>
                  <div className="cw-actions">
                     <button
                        type="button"
                        className="cw-button"
                        disabled={steps[0].id === current.id}
                        onClick={() =>
                           change(
                              moveCadence(
                                 steps,
                                 current.id,
                                 steps[
                                    steps.findIndex(
                                       (s) => s.id === current.id
                                    ) - 1
                                 ].id
                              )
                           )
                        }
                     >
                        Move earlier
                     </button>
                     <button
                        type="button"
                        className="cw-button"
                        disabled={steps[steps.length - 1].id === current.id}
                        onClick={() =>
                           change(
                              moveCadence(
                                 steps,
                                 current.id,
                                 steps[
                                    steps.findIndex(
                                       (s) => s.id === current.id
                                    ) + 1
                                 ].id
                              )
                           )
                        }
                     >
                        Move later
                     </button>
                     <button
                        type="button"
                        className="cw-link text-red-300"
                        onClick={() => {
                           change(
                              renumber(steps.filter((s) => s.id !== current.id))
                           )
                           setSelected(null)
                        }}
                     >
                        Remove step
                     </button>
                     <button
                        className="cw-button primary"
                        type="button"
                        onClick={() => setSelected(null)}
                     >
                        Done
                     </button>
                  </div>
               </div>
            </div>
         )}
         <p className="cw-inline-note flex items-center gap-2">
            <FlowIcon name="clock" size={14} />
            Calling hours come from your organization. All times use its
            campaign timezone.
         </p>
      </div>
   )
}
